import { useState, useEffect } from 'react';
import { Search } from 'lucide-react';
import Navbar from '../components/Navbar';
import ServerCard from '../components/ServerCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import api from '../utils/api';

export default function ServerListing() {
  const [servers, setServers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchServers();
  }, [selectedCategory, search, page]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchServers = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 12,
        status_filter: 'approved',
        featured: false
      };
      if (selectedCategory) params.category = selectedCategory;
      if (search) params.search = search;
      
      const response = await api.get('/servers', { params });
      setServers(response.data.servers);
      setTotalPages(response.data.pages);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching servers:', error);
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setPage(1);
  };

  return (
    <div className="min-h-screen noise-bg">
      <Navbar />
      
      <div className="pt-32 pb-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {/* Header */}
          <div className="mb-12">
            <h1 className="text-5xl md:text-7xl font-black tracking-tighter uppercase mb-4" style={{fontFamily: 'Unbounded, sans-serif'}} data-testid="servers-page-title">
              BROWSE <span className="text-[#00E055]">SERVERS</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Discover the best Minecraft servers in Kerala
            </p>
          </div>

          {/* Search & Filters */}
          <div className="space-y-6 mb-8">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search servers by name or description..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-12 h-14 bg-muted border-border focus:border-[#00E055] focus:ring-1 focus:ring-[#00E055] text-foreground"
                data-testid="search-input"
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => handleCategoryChange('')}
                variant={selectedCategory === '' ? 'default' : 'outline'}
                size="sm"
                className={selectedCategory === '' ? 'bg-[#00E055] text-black hover:bg-[#00E055]/90' : 'border-border hover:bg-accent'}
                data-testid="filter-all"
              >
                ALL
              </Button>
              {categories.map((cat) => (
                <Button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.name)}
                  variant={selectedCategory === cat.name ? 'default' : 'outline'}
                  size="sm"
                  className={selectedCategory === cat.name ? 'bg-[#00E055] text-black hover:bg-[#00E055]/90' : 'border-border hover:bg-accent'}
                  data-testid={`filter-${cat.slug}`}
                >
                  {cat.name.toUpperCase()}
                </Button>
              ))}
            </div>
          </div>

          {/* Servers Grid */}
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block w-12 h-12 border-4 border-[#00E055] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : servers.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12" data-testid="servers-grid">
                {servers.map((server) => (
                  <ServerCard key={server.id} server={server} />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2" data-testid="pagination">
                  <Button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    variant="outline"
                    data-testid="prev-page-button"
                  >
                    PREVIOUS
                  </Button>
                  <span className="px-4 py-2 text-sm font-medium" data-testid="page-info">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    variant="outline"
                    data-testid="next-page-button"
                  >
                    NEXT
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20">
              <p className="text-muted-foreground text-lg">No servers found. Try adjusting your filters.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
